import * as React from "react";

import { useDataMutationMyBagItemChangeQuantity } from "../data-mutation--mybag-item-change-quantity";
import { useDataMutationMyBagItemRemove } from "../data-mutation--mybag-item-remove";
import { useDataMutationMyBagPromoCodeApply } from "../data-mutation--mybag-promo-code-apply";
import { useDataMutationMyBagPromoCodeRemove } from "../data-mutation--mybag-promo-code-remove";
import { useDataSourceMyBagQuery } from "../data-source--mybag-provider";
import { useDataSourceMyBagTotalsQuery } from "../data-source--mybag-totals-provider";
import { useDataMutationMyBagGiftcardApply } from "../data-mutation--mybag-giftcard-apply";
import { useDataMutationMyBagGiftcardRemove } from "../data-mutation--mybag-giftcard-remove";

interface MyBagLoadingStateConsumerInterface {
  isLoading: boolean;
}
const defaultValue = {} as unknown as MyBagLoadingStateConsumerInterface;
const MyBagLoadingStateContext = React.createContext(defaultValue);

export const MyBagLoadingStateProvider: React.FunctionComponent = (props) => {
  const { status: myBagQueryStatus } = useDataSourceMyBagQuery();
  const { status: myBagTotalsQueryStatus } = useDataSourceMyBagTotalsQuery();
  const { status: myBagItemRemoveStatus } = useDataMutationMyBagItemRemove();
  const { status: myBagItemChangeQuantityStatus } =
    useDataMutationMyBagItemChangeQuantity();
  const { status: myBagPromoCodeApplyStatus } =
    useDataMutationMyBagPromoCodeApply();
  const { status: myBagPromoCodeRemoveStatus } =
    useDataMutationMyBagPromoCodeRemove();
  const { status: myBagGiftcardApplyStatus } =
    useDataMutationMyBagGiftcardApply();
  const { status: myBagGiftcardRemoveStatus } =
    useDataMutationMyBagGiftcardRemove();

  const isLoading: MyBagLoadingStateConsumerInterface["isLoading"] =
    React.useMemo(() => {
      return (
        myBagQueryStatus === "loading" ||
        myBagTotalsQueryStatus === "loading" ||
        myBagItemRemoveStatus === "loading" ||
        myBagItemChangeQuantityStatus === "loading" ||
        myBagPromoCodeApplyStatus === "loading" ||
        myBagPromoCodeRemoveStatus === "loading" ||
        myBagGiftcardApplyStatus === "loading" ||
        myBagGiftcardRemoveStatus === "loading"
      );
    }, [
      myBagQueryStatus,
      myBagTotalsQueryStatus,
      myBagItemRemoveStatus,
      myBagItemChangeQuantityStatus,
      myBagPromoCodeApplyStatus,
      myBagPromoCodeRemoveStatus,
      myBagGiftcardApplyStatus,
      myBagGiftcardRemoveStatus,
    ]);

  const value: MyBagLoadingStateConsumerInterface = React.useMemo(
    () => ({
      isLoading,
    }),
    [isLoading]
  );
  return (
    <MyBagLoadingStateContext.Provider value={value}>
      {props.children || []}
    </MyBagLoadingStateContext.Provider>
  );
};

export const useMyBagLoadingState = () => {
  const context = React.useContext(MyBagLoadingStateContext);
  if (context === defaultValue) {
    throw new Error(
      "useMyBagLoadingState must be used within a MyBagLoadingStateProvider"
    );
  }
  return context;
};
